import SidebarHeader from "@src/features/sidebars/ui/SidebarHeader";
import useLocations from "@src/features/locations/hooks/useLocations";
import { useReportingMockData } from "@src/features/reporting/ReportingMockDataContext";
import { buildOrganizationResultsNewStoreOpeningsMockData } from "@src/features/reporting/reportingMockData";
import CircleInfoOutlineIcon from "@src/lib/icons/18px/circle-info-outline";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import Text from "@src/lib/ui/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import React, { useMemo, useState } from "react";
import {
  AddOpeningDetailsDialog,
  DrawerDetailCard,
  DrawerEmptyCard,
  type StoreOpening,
} from "./NewStoreOpeningsShared";
import {
  type ImpactReportingNewStoreOpeningsQuery,
  useImpactReportingNewStoreOpeningsQuery,
} from "./ResultsWidgetsQuery.generated";

type Props = {
  showHeader?: boolean;
  dataOverride?: ImpactReportingNewStoreOpeningsQuery["ImpactReportingNewStoreOpenings"];
};

const NewStoreOpeningsListSidebar: React.FC<Props> = ({
  showHeader = true,
  dataOverride,
}) => {
  const { useMockData } = useReportingMockData();
  const { locations } = useLocations();
  const { data, loading } = useImpactReportingNewStoreOpeningsQuery({
    skip: !!dataOverride || useMockData,
  });
  const [editingOpening, setEditingOpening] = useState<StoreOpening | null>(
    null,
  );
  const isLoading = !dataOverride && loading;
  const mockData = useMemo(
    () =>
      useMockData
        ? buildOrganizationResultsNewStoreOpeningsMockData(
            locations.map((location) => ({
              id: location.id,
              name: location.name,
            })),
          )
        : undefined,
    [locations, useMockData],
  );
  const sourceOpenings = useMemo(
    () =>
      dataOverride ?? mockData ?? data?.ImpactReportingNewStoreOpenings ?? [],
    [dataOverride, mockData, data?.ImpactReportingNewStoreOpenings],
  );

  const allOpenings = useMemo(
    () =>
      [...sourceOpenings].sort((a, b) => {
        const dateA = a.openingDate ?? a.firstMemberAddedAt ?? "";
        const dateB = b.openingDate ?? b.firstMemberAddedAt ?? "";
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.localeCompare(dateA);
      }),
    [sourceOpenings],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {showHeader && <SidebarHeader />}
      <div className="flex items-center gap-1 p-4 pt-6 pb-2">
        <Text variant="body-title">All new store openings</Text>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex cursor-default align-middle">
                <CircleInfoOutlineIcon className="size-3 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px]">
              Showing all evaluated openings and all locations added in the last
              90 days
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <ScrollableFlexGrow className="min-h-0" innerClassName="p-4 pb-0">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-32 animate-pulse rounded-xl border border-gray-2 bg-gray-1"
              />
            ))}
          </div>
        ) : allOpenings.length === 0 ? (
          <div className="flex flex-1 items-start justify-center px-4 pt-24">
            <Text
              multiline
              variant="compact-description"
              className="max-w-[280px] text-center text-gray-8"
            >
              No new locations have been added to Opus in the last 90 days
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-2 pb-4">
            {allOpenings.map((opening) =>
              opening.openingDate ? (
                <DrawerDetailCard
                  key={opening.id ?? opening.locationId}
                  opening={opening}
                  onEdit={() => setEditingOpening(opening)}
                />
              ) : (
                <DrawerEmptyCard
                  key={opening.id ?? opening.locationId}
                  opening={opening}
                  onEdit={() => setEditingOpening(opening)}
                />
              ),
            )}
          </div>
        )}
      </ScrollableFlexGrow>

      {editingOpening && (
        <AddOpeningDetailsDialog
          opening={editingOpening}
          open={!!editingOpening}
          isDemoMode={useMockData}
          onOpenChange={(open) => {
            if (!open) setEditingOpening(null);
          }}
        />
      )}
    </div>
  );
};

export default NewStoreOpeningsListSidebar;
